import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type RefObject,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { ArrowDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  distributeColumnWidths,
  isMeasured,
  pinnedCount,
  resolveTrack,
  type ColumnMeasure,
  type TableColumn,
} from './tableColumns';
import './Table.css';

/* ----------------------------------------
 * The one table: the parts below, in two modes.
 *
 * Plain: the parts as the product's tables use them today (the
 * dashboard, the Team and guest pages, inside the Claim Chart). Widths
 * come from the caller's classes; the rules are on the rows.
 *
 * Declared columns (`columns`) or a sticky header: the data-grid mode.
 * The table fills the box it sits in and sizes its columns by the
 * grammar in tableColumns.ts; cells carry the rules, so a sticky header
 * and pinned columns keep them. The box that scrolls, its height and the
 * sticky offset (--table-sticky-top) are the caller's: TableContainer
 * is the usual box. Table.css draws the sticky, pinned and focus parts.
 * Storybook: Components/Table.
 * ---------------------------------------- */

export type TableDensity = 'regular' | 'compact';
export type TableFrame = 'bare' | 'framed';
export type TableSortDirection = 'ascending' | 'descending' | 'none';

interface ColumnInfo {
  column: TableColumn;
  index: number;
  pinned: boolean;
  pinEdge: boolean;
}

interface TableContextValue {
  density: TableDensity;
  /** Cells carry the rules (declared columns, or a sticky header). */
  grid: boolean;
  columns: Map<string, ColumnInfo> | null;
  count: number | null;
}

const TableContext = createContext<TableContextValue>({ density: 'regular', grid: false, columns: null, count: null });

// Cell padding by density: regular is the product's 12 by 16, compact the
// dense 8 by 12. The header keeps 8 above and below at either. A control
// column (a checkbox, a row menu) takes the tight 8 at the sides at
// either: its control carries its own room. One class per axis, chosen
// here: tailwind-merge does not see two ds-* spacing classes as rivals,
// so a second px- class would not replace the first.
const PADDING = {
  regular: { head: 'py-ds-2', body: 'py-ds-3', x: 'px-ds-4' },
  compact: { head: 'py-ds-2', body: 'py-ds-2', x: 'px-ds-3' },
} as const;
const CONTROL_X = 'px-ds-2';

const ALIGN = { start: undefined, center: 'text-center', end: 'text-right tabular-nums' } as const;

/* ----------------------------------------
 * Column layout: measure what is sized by content, then share the width.
 * ---------------------------------------- */

// Body rows read for content-sized columns. Enough to find the widest
// control or date; not every row of a long table.
const SAMPLE_ROWS = 25;

function measureColumns(table: HTMLTableElement, columns: TableColumn[]): ColumnMeasure[] {
  const index = new Map(columns.map((c, i) => [c.id, i]));
  const out: ColumnMeasure[] = columns.map(() => ({ padding: 0, header: 0, content: 0 }));
  const probes: [number, 'header' | 'content', HTMLElement][] = [];
  // Each measured cell's content is copied into an invisible max-content
  // box inside the cell, so it inherits the cell's type, and every box is
  // read in one layout, then removed before paint.
  const probe = (cell: HTMLElement, i: number, slot: 'header' | 'content') => {
    const box = document.createElement('div');
    box.setAttribute('aria-hidden', 'true');
    box.style.cssText =
      'position:absolute;left:0;top:0;visibility:hidden;pointer-events:none;width:max-content;white-space:nowrap;';
    cell.childNodes.forEach((n) => box.appendChild(n.cloneNode(true)));
    cell.appendChild(box);
    probes.push([i, slot, box]);
  };
  table.tHead?.querySelectorAll<HTMLElement>('[data-column]').forEach((cell) => {
    const i = index.get(cell.dataset.column ?? '');
    if (i === undefined) return;
    const cs = getComputedStyle(cell);
    out[i].padding = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    probe(cell, i, 'header');
  });
  const rows = Array.from(table.tBodies[0]?.rows ?? []).slice(0, SAMPLE_ROWS);
  for (const row of rows) {
    for (const cell of Array.from(row.cells)) {
      const i = index.get(cell.dataset.column ?? '');
      if (i !== undefined && isMeasured(columns[i])) probe(cell, i, 'content');
    }
  }
  for (const [i, slot, box] of probes) out[i][slot] = Math.max(out[i][slot], box.getBoundingClientRect().width);
  for (const [, , box] of probes) box.remove();
  return out;
}

function useColumnLayout(tableRef: RefObject<HTMLTableElement | null>, columns: TableColumn[] | undefined) {
  const [widths, setWidths] = useState<number[] | null>(null);
  const columnsRef = useRef(columns);
  const measures = useRef<ColumnMeasure[] | null>(null);

  const update = useCallback(() => {
    const table = tableRef.current;
    const cols = columnsRef.current;
    const m = measures.current;
    const parent = table?.parentElement;
    if (!cols || !m || !parent || m.length !== cols.length) return;
    const cs = getComputedStyle(parent);
    const available = parent.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    const next = distributeColumnWidths(
      cols.map((c, i) => resolveTrack(c, m[i])),
      Math.max(0, available)
    );
    setWidths((prev) => (prev && prev.length === next.length && prev.every((w, i) => w === next[i]) ? prev : next));
  }, [tableRef]);

  // Every render: rows may have arrived or changed. One layout read.
  useLayoutEffect(() => {
    columnsRef.current = columns;
    if (!columns || !tableRef.current) return;
    measures.current = measureColumns(tableRef.current, columns);
    update();
  });

  // The box the table sits in decides the width to share.
  useLayoutEffect(() => {
    const parent = tableRef.current?.parentElement;
    if (!columns || !parent || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => update());
    observer.observe(parent);
    return () => observer.disconnect();
  }, [columns, tableRef, update]);

  return columns ? widths : null;
}

/* ----------------------------------------
 * Table
 * ---------------------------------------- */
export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /** The columns, in order, by kind (tableColumns.ts). Define them once,
   *  outside render. Cells name theirs with `column`. */
  columns?: TableColumn[];
  /** `regular` (12 by 16) or `compact` (8 by 12). */
  density?: TableDensity;
  /** The header stays at the top of the box that scrolls, at
   *  --table-sticky-top (0 unless the caller sets it). */
  stickyHeader?: boolean;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, columns, density = 'regular', stickyHeader = false, style, children, ...props }, ref) => {
    const inner = useRef<HTMLTableElement | null>(null);
    const setRef = useCallback(
      (node: HTMLTableElement | null) => {
        inner.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );
    const widths = useColumnLayout(inner, columns);
    const grid = !!columns || stickyHeader;
    const pins = columns ? pinnedCount(columns) : 0;

    const context = useMemo<TableContextValue>(
      () => ({
        density,
        grid,
        count: columns?.length ?? null,
        columns: columns
          ? new Map(columns.map((c, i) => [c.id, { column: c, index: i, pinned: i < pins, pinEdge: i === pins - 1 }]))
          : null,
      }),
      [density, grid, columns, pins]
    );

    // The measured widths, and the pinned columns' offsets as variables
    // the pinned cells read, so a resize redraws without re-rendering rows.
    let layout: CSSProperties | undefined;
    if (widths) {
      const vars: Record<string, string> = {};
      let left = 0;
      for (let i = 0; i < pins; i++) {
        vars[`--ds-table-pin-${i}`] = `${left}px`;
        left += widths[i];
      }
      layout = { width: widths.reduce((a, b) => a + b, 0), ...vars };
    }

    return (
      <TableContext.Provider value={context}>
        <table
          ref={setRef}
          data-grid={grid || undefined}
          data-sticky-header={stickyHeader || undefined}
          data-pinned={pins > 0 || undefined}
          className={cn(
            'ds-table w-full table-fixed type-ui',
            grid ? 'border-separate border-spacing-0' : 'border-collapse',
            className
          )}
          style={layout ? { ...layout, ...style } : style}
          {...props}
        >
          {columns && (
            <colgroup>
              {columns.map((c, i) => (
                <col key={c.id} style={widths ? { width: widths[i] } : undefined} />
              ))}
            </colgroup>
          )}
          {children}
        </table>
      </TableContext.Provider>
    );
  }
);

Table.displayName = 'Table';

/* ----------------------------------------
 * Table Container: the box a table scrolls in, and its frame
 * ---------------------------------------- */
export interface TableContainerProps extends HTMLAttributes<HTMLDivElement> {
  /** `bare`: the page, card or composition draws the edge. `framed`: a
   *  1px square edge of the table rule, on the table surface. */
  frame?: TableFrame;
}

/** Scrolls both ways when the caller bounds its height; otherwise only
 *  sideways, when the table is wider. A sticky header sticks to it, so a
 *  header meant to stick to the page goes without one. */
export const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  ({ frame = 'bare', className, onScroll, ...props }, ref) => (
    <div
      ref={ref}
      data-frame={frame}
      className={cn(
        'ds-table-container relative overflow-auto',
        frame === 'framed' && 'border border-table-rule bg-table-surface',
        className
      )}
      onScroll={(e) => {
        // Pinned columns draw their edge once content passes beneath them.
        e.currentTarget.toggleAttribute('data-scrolled-x', e.currentTarget.scrollLeft > 0);
        onScroll?.(e);
      }}
      {...props}
    />
  )
);

TableContainer.displayName = 'TableContainer';

/* ----------------------------------------
 * Table Header
 * ---------------------------------------- */
export type TableHeaderProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, ...props }, ref) => {
    const { grid } = useContext(TableContext);
    return (
      <thead
        ref={ref}
        className={cn(!grid && 'border-b border-table-rule', 'bg-table-head', className)}
        {...props}
      />
    );
  }
);

TableHeader.displayName = 'TableHeader';

/* ----------------------------------------
 * Table Body
 * ---------------------------------------- */
export type TableBodyProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={className} {...props} />
);

TableBody.displayName = 'TableBody';

/* ----------------------------------------
 * Table Row
 * ---------------------------------------- */
export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** The row opens or selects something: pointer cursor and the hover fill.
   *  With an onClick, Enter and Space press it when the row has focus
   *  (give it tabIndex={0} to put it in the Tab order). */
  interactive?: boolean;
  /** Selected or current: the one row chosen or open in a panel. The selected
   *  fill, held under the pointer. A checked row in a multi-select is not
   *  this: its checkbox shows it. */
  selected?: boolean;
  /** Briefly marked (a citation jump): the header band's fill. */
  highlighted?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, interactive, selected, highlighted, onKeyDown, ...props }, ref) => {
    const { grid } = useContext(TableContext);
    const presses = interactive && !!props.onClick;
    return (
      <tr
        ref={ref}
        className={cn(
          !grid && 'border-b border-table-rule',
          interactive && 'cursor-pointer transition-colors duration-150 hover:bg-table-hover',
          highlighted && 'bg-table-head',
          selected && 'bg-table-selected hover:bg-table-selected',
          className
        )}
        onKeyDown={
          presses
            ? (e) => {
                onKeyDown?.(e);
                if (e.defaultPrevented || e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.currentTarget.click();
                }
              }
            : onKeyDown
        }
        {...props}
      />
    );
  }
);

TableRow.displayName = 'TableRow';

/* ----------------------------------------
 * Cells: shared by the header and body cells
 * ---------------------------------------- */
function useColumn(column: string | undefined) {
  const { density, columns } = useContext(TableContext);
  return { density, info: column ? columns?.get(column) : undefined };
}

function pinProps(info: ColumnInfo | undefined, style: CSSProperties | undefined) {
  if (!info?.pinned) return { style };
  return {
    'data-pin': '',
    'data-pin-edge': info.pinEdge ? '' : undefined,
    style: { left: `var(--ds-table-pin-${info.index}, 0px)`, ...style },
  };
}

/* ----------------------------------------
 * Table Header Cell
 * ---------------------------------------- */
export interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** The column's id, from the table's `columns`. */
  column?: string;
  /** The column's sort state, for a sortable column. Controlled: the caller sorts. */
  sort?: TableSortDirection;
  /** Makes the label a sort button. */
  onSort?: () => void;
}

export const TableHeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  ({ className, column, sort, onSort, style, children, ...props }, ref) => {
    const { density, info } = useColumn(column);
    // In an end-aligned column the arrow leads, so the label ends on the figures' edge.
    const leads = info?.column.align === 'end';
    const arrow = onSort ? <ArrowDown aria-hidden className="ds-table__sort-icon" /> : null;
    return (
      <th
        ref={ref}
        data-column={column}
        aria-sort={sort === 'ascending' || sort === 'descending' ? sort : undefined}
        className={cn(
          'text-left font-medium text-ds-text-secondary',
          PADDING[density].head,
          info?.column.kind === 'control' ? CONTROL_X : PADDING[density].x,
          info && 'whitespace-nowrap',
          ALIGN[info?.column.align ?? 'start'],
          className
        )}
        {...pinProps(info, style)}
        {...props}
      >
        {onSort ? (
          <button type="button" className={leads ? 'ds-table__sort ds-table__sort--leads' : 'ds-table__sort'} onClick={onSort}>
            {leads && arrow}
            {children}
            {!leads && arrow}
          </button>
        ) : (
          children
        )}
      </th>
    );
  }
);

TableHeaderCell.displayName = 'TableHeaderCell';

/* ----------------------------------------
 * Table Cell
 * ---------------------------------------- */
export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** The column's id, from the table's `columns`. */
  column?: string;
  /** One line per block, ending in an ellipsis. Defaults to the column's. */
  truncate?: boolean;
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, column, truncate, style, ...props }, ref) => {
    const { density, info } = useColumn(column);
    const kind = info?.column.kind;
    return (
      <td
        ref={ref}
        data-column={column}
        className={cn(
          PADDING[density].body,
          kind === 'control' ? CONTROL_X : PADDING[density].x,
          'text-ds-text',
          ALIGN[info?.column.align ?? 'start'],
          (truncate ?? info?.column.truncate) && 'ds-table__truncate',
          // Controls and short values are given width, never truncated.
          (kind === 'control' || kind === 'constrained') && 'whitespace-nowrap',
          className
        )}
        {...pinProps(info, style)}
        {...props}
      />
    );
  }
);

TableCell.displayName = 'TableCell';

/* ----------------------------------------
 * Table Empty: one row across every column
 * ---------------------------------------- */
export type TableEmptyProps = TdHTMLAttributes<HTMLTableCellElement>;

/** Inside TableBody, in place of the rows. Spans the declared columns, or
 *  `colSpan` for a table without them. */
export const TableEmpty = forwardRef<HTMLTableCellElement, TableEmptyProps>(
  ({ className, colSpan, ...props }, ref) => {
    const { count } = useContext(TableContext);
    return (
      <tr className="ds-table__empty">
        <td
          ref={ref}
          colSpan={colSpan ?? count ?? 1}
          className={cn('py-ds-8 px-ds-4 text-center text-ds-text-secondary', className)}
          {...props}
        />
      </tr>
    );
  }
);

TableEmpty.displayName = 'TableEmpty';
